import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Loader2,
  Clock,
  MapPin,
  User,
  Car,
  Users,
  AlertTriangle,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { CompromissoModal } from '../../components/agenda/CompromissoModal';
import { formatDate, formatTime } from '../../utils/format';
import { Button, buttonClass, EmptyState, card } from '../../design-system';

// Error Boundary para capturar erros de renderização
class AgendaErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[AgendaErrorBoundary] Error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertTriangle className="w-6 h-6" />
            <h2 className="text-lg font-semibold">Erro na Agenda</h2>
          </div>
          <p className="text-red-700 mb-2">
            Ocorreu um erro ao renderizar a agenda:
          </p>
          <pre className="bg-red-100 p-4 rounded text-sm overflow-auto">
            {this.state.error?.message}
            {'\n'}
            {this.state.error?.stack}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Recarregar Página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

interface CalendarioEvento {
  id: number;
  title: string;
  start: string;
  end: string | null;
  allDay: boolean;
  backgroundColor: string;
  borderColor: string;
  tipo: string | null;
  status: string;
  local: string | null;
  usuarioNome: string | null;
  leadNome: string | null;
  veiculoDescricao: string | null;
}

interface TipoCompromisso {
  id: number;
  codigo: string;
  nome: string;
  cor: string;
  icone: string | null;
}

type ViewMode = 'month' | 'week' | 'day' | 'list';

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const DIAS_SEMANA_FULL = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
const MESES = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

// Wrapper com Error Boundary
export function Agenda() {
  return (
    <AgendaErrorBoundary>
      <AgendaContent />
    </AgendaErrorBoundary>
  );
}

function AgendaContent() {
  const { showError } = useNotification();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [eventos, setEventos] = useState<CalendarioEvento[]>([]);
  const [tipos, setTipos] = useState<TipoCompromisso[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvento, setSelectedEvento] = useState<CalendarioEvento | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);

  // Calcular range de datas baseado na view
  const dateRange = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (viewMode === 'month') {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      // Ajustar para comecar no domingo da semana
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - startDate.getDay());
      // Ajustar para terminar no sabado da semana
      const endDate = new Date(lastDay);
      endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
      return { start: startDate, end: endDate };
    } else if (viewMode === 'week') {
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - startDate.getDay());
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
      return { start: startDate, end: endDate };
    } else {
      return { start: currentDate, end: currentDate };
    }
  }, [currentDate, viewMode]);

  const fetchEventos = useCallback(async () => {
    setIsLoading(true);
    try {
      const startStr = dateRange.start.toISOString();
      const endStr = new Date(dateRange.end.getTime() + 86400000).toISOString();
      const response = await api.get<CalendarioEvento[]>(
        `/agenda/calendario?dataInicio=${startStr}&dataFim=${endStr}`
      );
      setEventos(response.data);
    } catch {
      setComponentError('Erro ao carregar eventos');
      showError('Erro ao carregar eventos');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, showError]);

  const fetchTipos = useCallback(async () => {
    try {
      const response = await api.get<TipoCompromisso[]>('/agenda/tipos');
      setTipos(response.data);
    } catch {
      // Silenciar erro de tipos
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchEventos();
    return () => controller.abort();
  }, [fetchEventos]);

  useEffect(() => {
    fetchTipos();
  }, [fetchTipos]);

  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setSelectedEvento(null);
    setModalOpen(true);
  };

  const handleEventClick = (evento: CalendarioEvento) => {
    setSelectedEvento(evento);
    setSelectedDate(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDate(null);
    setSelectedEvento(null);
  };

  const handleModalSave = () => {
    fetchEventos();
    handleModalClose();
  };

  // Gerar dias do calendario mensal
  const calendarDays = useMemo(() => {
    const days: Date[] = [];
    const current = new Date(dateRange.start);
    while (current <= dateRange.end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  }, [dateRange]);

  // Agrupar eventos por data
  const eventosPorData = useMemo(() => {
    const map = new Map<string, CalendarioEvento[]>();
    eventos.forEach(evento => {
      const dateKey = new Date(evento.start).toDateString();
      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)!.push(evento);
    });
    return map;
  }, [eventos]);

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const getTitle = () => {
    if (viewMode === 'month') {
      return `${MESES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (viewMode === 'week') {
      const start = dateRange.start;
      const end = dateRange.end;
      if (start.getMonth() === end.getMonth()) {
        return `${start.getDate()} - ${end.getDate()} de ${MESES[start.getMonth()]} ${start.getFullYear()}`;
      }
      return `${start.getDate()} ${MESES[start.getMonth()].slice(0, 3)} - ${end.getDate()} ${MESES[end.getMonth()].slice(0, 3)} ${end.getFullYear()}`;
    } else {
      return `${currentDate.getDate()} de ${MESES[currentDate.getMonth()]} de ${currentDate.getFullYear()}`;
    }
  };

  // Exibir erro se houver
  if (componentError) {
    return (
      <div className="p-8 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-3 text-yellow-600 mb-4">
          <AlertTriangle className="w-6 h-6" />
          <h2 className="text-lg font-semibold">Aviso</h2>
        </div>
        <p className="text-yellow-700">{componentError}</p>
        <button
          onClick={() => {
            setComponentError(null);
            fetchEventos();
          }}
          className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <PageHeader title="Agenda" subtitle="Gerencie seus compromissos e agendamentos">
        <Button onClick={() => { setSelectedDate(new Date()); setModalOpen(true); }}>
          <Plus className="w-4 h-4" />
          Novo Compromisso
        </Button>
      </PageHeader>

      {/* Toolbar */}
      <div className={`${card} p-4 mb-4`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button onClick={goToToday} className={buttonClass('secondary', 'sm')}>
              Hoje
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={navigatePrev}
                className="p-1.5 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={navigateNext}
                className="p-1.5 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <h2 className="text-lg font-bold tracking-tight text-slate-900 ml-2">
              {getTitle()}
            </h2>
          </div>

          {/* View Mode */}
          <div className="flex items-center gap-1">
            {(['month', 'week', 'day', 'list'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`${buttonClass('secondary', 'sm')} ${
                  viewMode === mode ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700' : ''
                }`}
              >
                {mode === 'month' ? 'Mes' : mode === 'week' ? 'Semana' : mode === 'day' ? 'Dia' : 'Lista'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar Content */}
      <div className={`${card} overflow-hidden min-h-[500px]`}>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : viewMode === 'month' ? (
          <MonthView
            days={calendarDays}
            eventosPorData={eventosPorData}
            isToday={isToday}
            isCurrentMonth={isCurrentMonth}
            onDateClick={handleDateClick}
            onEventClick={handleEventClick}
          />
        ) : viewMode === 'week' ? (
          <WeekView
            startDate={dateRange.start}
            eventosPorData={eventosPorData}
            isToday={isToday}
            onDateClick={handleDateClick}
            onEventClick={handleEventClick}
          />
        ) : viewMode === 'day' ? (
          <DayView
            date={currentDate}
            eventos={eventosPorData.get(currentDate.toDateString()) || []}
            onEventClick={handleEventClick}
            onTimeClick={handleDateClick}
          />
        ) : (
          <ListView
            eventos={eventos}
            onEventClick={handleEventClick}
          />
        )}
      </div>

      {/* Modal de Compromisso */}
      {modalOpen && (
        <CompromissoModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          eventoId={selectedEvento?.id}
          dataInicial={selectedDate}
          tipos={tipos}
        />
      )}
    </div>
  );
}

// ==================== MONTH VIEW ====================

interface MonthViewProps {
  days: Date[];
  eventosPorData: Map<string, CalendarioEvento[]>;
  isToday: (date: Date) => boolean;
  isCurrentMonth: (date: Date) => boolean;
  onDateClick: (date: Date) => void;
  onEventClick: (evento: CalendarioEvento) => void;
}

function MonthView({ days, eventosPorData, isToday, isCurrentMonth, onDateClick, onEventClick }: MonthViewProps) {
  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="grid grid-cols-7 border-b border-slate-200">
        {DIAS_SEMANA.map((dia) => (
          <div key={dia} className="py-2 text-center text-xs font-bold uppercase tracking-wider text-slate-400">
            {dia}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="flex-1 grid grid-cols-7 auto-rows-fr">
        {days.map((day, index) => {
          const dayEvents = eventosPorData.get(day.toDateString()) || [];
          const today = isToday(day);
          const currentMonth = isCurrentMonth(day);

          return (
            <div
              key={index}
              className={`border-b border-r border-slate-100 p-1 min-h-[100px] cursor-pointer hover:bg-slate-50 transition-colors ${
                !currentMonth ? 'bg-slate-50/60' : ''
              }`}
              onClick={() => onDateClick(day)}
            >
              <div className="flex justify-between items-start mb-1">
                <span
                  className={`inline-flex items-center justify-center w-7 h-7 text-sm rounded-full tabular-nums ${
                    today
                      ? 'bg-primary-600 text-white font-semibold'
                      : currentMonth
                      ? 'text-slate-900'
                      : 'text-slate-400'
                  }`}
                >
                  {day.getDate()}
                </span>
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((evento) => (
                  <div
                    key={evento.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick(evento); }}
                    className="text-xs px-1.5 py-0.5 rounded truncate cursor-pointer bg-slate-50 text-slate-700 hover:bg-slate-100"
                    style={{ borderLeft: `3px solid ${evento.backgroundColor}` }}
                  >
                    {evento.allDay ? '' : formatTime(evento.start) + ' '}{evento.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-xs text-slate-500 px-1">
                    +{dayEvents.length - 3} mais
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== WEEK VIEW ====================

interface WeekViewProps {
  startDate: Date;
  eventosPorData: Map<string, CalendarioEvento[]>;
  isToday: (date: Date) => boolean;
  onDateClick: (date: Date) => void;
  onEventClick: (evento: CalendarioEvento) => void;
}

function WeekView({ startDate, eventosPorData, isToday, onDateClick, onEventClick }: WeekViewProps) {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startDate);
    day.setDate(day.getDate() + i);
    days.push(day);
  }

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="flex flex-col overflow-auto max-h-[600px]">
      {/* Header */}
      <div className="grid grid-cols-8 border-b border-slate-200 sticky top-0 bg-white z-10">
        <div className="py-2 px-2 text-xs text-slate-400"></div>
        {days.map((day, index) => {
          const today = isToday(day);
          return (
            <div key={index} className="py-2 text-center border-l border-slate-200">
              <div className="text-xs text-slate-400">{DIAS_SEMANA[day.getDay()]}</div>
              <div className={`text-lg font-bold tabular-nums ${today ? 'text-primary-600' : 'text-slate-900'}`}>
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time Grid */}
      <div className="flex-1">
        {hours.map((hour) => (
          <div key={hour} className="grid grid-cols-8 border-b border-slate-100" style={{ minHeight: '60px' }}>
            <div className="py-1 px-2 text-xs text-slate-400 text-right tabular-nums">
              {hour.toString().padStart(2, '0')}:00
            </div>
            {days.map((day, index) => {
              const dayEvents = eventosPorData.get(day.toDateString()) || [];
              const hourEvents = dayEvents.filter(e => {
                const eventHour = new Date(e.start).getHours();
                return eventHour === hour;
              });

              return (
                <div
                  key={index}
                  className="border-l border-slate-200 p-0.5 cursor-pointer hover:bg-slate-50"
                  onClick={() => {
                    const dateTime = new Date(day);
                    dateTime.setHours(hour, 0, 0, 0);
                    onDateClick(dateTime);
                  }}
                >
                  {hourEvents.map((evento) => (
                    <div
                      key={evento.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick(evento); }}
                      className="text-xs px-1 py-0.5 rounded mb-0.5 truncate cursor-pointer bg-slate-50 text-slate-700 hover:bg-slate-100"
                      style={{ borderLeft: `3px solid ${evento.backgroundColor}` }}
                    >
                      {evento.title}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== DAY VIEW ====================

interface DayViewProps {
  date: Date;
  eventos: CalendarioEvento[];
  onEventClick: (evento: CalendarioEvento) => void;
  onTimeClick: (date: Date) => void;
}

function DayView({ date, eventos, onEventClick, onTimeClick }: DayViewProps) {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="overflow-auto max-h-[600px]">
      <div className="sticky top-0 bg-white z-10 p-4 border-b border-slate-200">
        <div className="text-lg font-bold tracking-tight text-slate-900">
          {DIAS_SEMANA_FULL[date.getDay()]}, {date.getDate()} de {MESES[date.getMonth()]}
        </div>
      </div>

      <div className="p-4">
        {hours.map((hour) => {
          const hourEvents = eventos.filter(e => {
            const eventHour = new Date(e.start).getHours();
            return eventHour === hour;
          });

          return (
            <div
              key={hour}
              className="flex border-b border-slate-100 min-h-[60px] cursor-pointer hover:bg-slate-50"
              onClick={() => {
                const dateTime = new Date(date);
                dateTime.setHours(hour, 0, 0, 0);
                onTimeClick(dateTime);
              }}
            >
              <div className="w-16 py-2 text-sm text-slate-400 text-right pr-3 tabular-nums">
                {hour.toString().padStart(2, '0')}:00
              </div>
              <div className="flex-1 py-1 pl-3 border-l border-slate-200">
                {hourEvents.map((evento) => (
                  <div
                    key={evento.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick(evento); }}
                    className="mb-1 p-2 rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100"
                    style={{ borderLeft: `4px solid ${evento.backgroundColor}` }}
                  >
                    <div className="font-medium text-slate-900">{evento.title}</div>
                    <div className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(evento.start)}
                      {evento.end && ` - ${formatTime(evento.end)}`}
                    </div>
                    {evento.local && (
                      <div className="text-sm text-slate-500 flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        {evento.local}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== LIST VIEW ====================

interface ListViewProps {
  eventos: CalendarioEvento[];
  onEventClick: (evento: CalendarioEvento) => void;
}

function ListView({ eventos, onEventClick }: ListViewProps) {
  // Agrupar por data
  const eventosPorData = useMemo(() => {
    const grouped = new Map<string, CalendarioEvento[]>();
    const sortedEventos = [...eventos].sort((a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    sortedEventos.forEach(evento => {
      const dateKey = formatDate(evento.start);
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(evento);
    });

    return grouped;
  }, [eventos]);

  if (eventos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <CalendarIcon className="w-12 h-12 mb-2 text-slate-300" />
        <EmptyState>Nenhum compromisso encontrado</EmptyState>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {Array.from(eventosPorData.entries()).map(([data, eventosData]) => (
        <div key={data}>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{data}</h3>
          <div className="space-y-2">
            {eventosData.map((evento) => (
              <div
                key={evento.id}
                onClick={() => onEventClick(evento)}
                className="bg-white border border-slate-200 rounded-xl p-4 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)]"
                style={{ borderLeft: `3px solid ${evento.backgroundColor}` }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-semibold text-slate-900">{evento.title}</h4>
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                        {evento.tipo || 'Compromisso'}
                      </span>
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="text-sm text-slate-600 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {evento.allDay ? 'Dia inteiro' : (
                          <>
                            {formatTime(evento.start)}
                            {evento.end && ` - ${formatTime(evento.end)}`}
                          </>
                        )}
                      </div>
                      {evento.local && (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {evento.local}
                        </div>
                      )}
                      {evento.usuarioNome && (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {evento.usuarioNome}
                        </div>
                      )}
                      {evento.leadNome && (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Lead: {evento.leadNome}
                        </div>
                      )}
                      {evento.veiculoDescricao && (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <Car className="w-4 h-4" />
                          {evento.veiculoDescricao}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
